import { useEffect, useRef } from 'react';

const featuredCase = {
  no: '01',
  name: '串カツ あき×ひろ 様',
  location: '熊本市',
  category: '飲食店',
  icon: 'ri-store-2-line',
  tags: ['WEBサイト制作', 'MEO対策', '月額保守', 'Readdy AI'],
  url: 'akihiro-kushikatsu.jp',
  features: [
    {
      icon: 'ri-rocket-2-line',
      label: 'Readdy AI を活用したスピード公開',
      desc: 'AIを駆使した効率的な制作フローで、打ち合わせから公開までを最短ルートで実現。ビジネスの立ち上がりを加速します。',
    },
    {
      icon: 'ri-map-pin-2-line',
      label: 'Googleマップ（MEO）対策による認知拡大',
      desc: 'Googleビジネスプロフィールの最適化・口コミ対策・写真整備により、地元エリアでの検索露出を大幅に向上させました。',
    },
    {
      icon: 'ri-shield-check-line',
      label: '月額保守による安定運用',
      desc: 'セキュリティ対応・コンテンツ更新・速度改善を月額定額でサポート。公開後も安心して運用を続けられる体制を整えています。',
    },
  ],
  result: '公開後3ヶ月でGoogleマップの露出が大幅増加、新規来客数が向上',
};

const subCases = [
  {
    no: '02',
    name: '美容院 E 様',
    location: '関西エリア',
    category: '美容院',
    icon: 'ri-scissors-cut-line',
    color: '#7C5CBF',
    tags: ['WEBサイト制作', 'MEO対策', 'Instagram連携'],
    result: '予約問い合わせ数が月間2倍以上に増加',
    resultIcon: 'ri-calendar-check-line',
  },
  {
    no: '03',
    name: 'ネイルサロン N 様',
    location: '九州エリア',
    category: 'ネイルサロン',
    icon: 'ri-palette-line',
    color: '#C06080',
    tags: ['WEBサイト制作', 'SNS運用代行', 'MEO対策'],
    result: 'SNS経由の新規顧客が3ヶ月で150%増',
    resultIcon: 'ri-instagram-line',
  },
  {
    no: '04',
    name: '整骨院 H 様',
    location: '関西エリア',
    category: '整骨院',
    icon: 'ri-heart-pulse-line',
    color: '#2E7D60',
    tags: ['WEBサイト制作', 'MEO対策'],
    result: 'Googleマップ検索順位が地域3位に浮上',
    resultIcon: 'ri-map-pin-line',
  },
  {
    no: '05',
    name: '歯科クリニック T 様',
    location: '大阪エリア',
    category: '歯科',
    icon: 'ri-hospital-line',
    color: '#1B6EA8',
    tags: ['WEBサイト制作', 'MEO対策', 'SEO対策'],
    result: '新規患者数が3ヶ月で約1.8倍に増加',
    resultIcon: 'ri-user-add-line',
  },
  {
    no: '06',
    name: 'パーソナルジム F 様',
    location: '関西エリア',
    category: 'パーソナルジム',
    icon: 'ri-run-line',
    color: '#C07820',
    tags: ['WEBサイト制作', 'SEO対策', 'MEO対策'],
    result: '体験予約がWEB経由で月間3倍に増加',
    resultIcon: 'ri-calendar-check-line',
  },
  {
    no: '07',
    name: 'エステサロン K 様',
    location: '九州エリア',
    category: 'エステ',
    icon: 'ri-sparkling-line',
    color: '#A0714F',
    tags: ['WEBサイト制作', 'SNS運用代行', 'Instagram連携'],
    result: 'Instagram経由の来店予約が2倍超に',
    resultIcon: 'ri-instagram-line',
  },
];

export default function CaseStudy() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.cs-fade').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="casestudy" className="py-28 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Section Header */}
        <div className="mb-16 md:mb-20 cs-fade opacity-0 translate-y-6 transition-all duration-700">
          <p className="text-[#1B2D5B] text-xs tracking-[0.3em] mb-4 uppercase font-medium">/ Case Study</p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0D1B2A] tracking-tight leading-tight mb-5">
            制作・支援実績
          </h2>
          <p className="text-[#555] text-lg leading-relaxed max-w-xl">
            実際にご支援したクライアントの事例をご紹介します。<br className="hidden md:block" />
            課題解決から運用まで、一貫してサポートしています。
          </p>
        </div>

        {/* Featured Case Card */}
        <div
          className="cs-fade opacity-0 translate-y-6 transition-all duration-700 delay-100 rounded-3xl overflow-hidden border border-[#E2E8F0] mb-10"
          style={{ background: 'linear-gradient(135deg, #F7F9FC 0%, #EEF2F9 100%)' }}
        >
          {/* Website Preview */}
          <div className="relative bg-[#0D1B2A] px-6 md:px-8 pt-6 pb-0 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=dark%20navy%20blue%20abstract%20geometric%20texture%20background%2C%20subtle%20glowing%20grid%20lines%2C%20elegant%20deep%20midnight%20blue%20professional%20design%2C%20soft%20light%20beams%2C%20premium%20dark%20backdrop%20for%20business%20presentation%2C%20minimalist%20dark%20blue%20atmosphere%20with%20faint%20bokeh%20lights&width=900&height=600&seq=casestudy-darkbg-01&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-center opacity-30"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A]/60 to-[#0D1B2A]/20" />
            </div>
            <div className="relative z-10 flex items-center gap-2 mb-3">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57] shrink-0" />
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E] shrink-0" />
              <span className="w-3 h-3 rounded-full bg-[#28C840] shrink-0" />
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-white/10 text-white/60 border border-white/10 ml-auto shrink-0">
                <i className={featuredCase.icon} />
                {featuredCase.category} / {featuredCase.location}
              </span>
            </div>
            <div className="relative z-10 rounded-t-xl overflow-hidden w-full h-[380px] bg-white">
              <iframe
                src="https://readdy.cc/preview/41d580cf-b466-4484-b234-e6188398962d/7862685/"
                title="串カツ あき×ひろ ウェブサイト"
                className="absolute top-0 left-0"
                style={{
                  width: '167%',
                  height: '167%',
                  transform: 'scale(0.6)',
                  transformOrigin: 'top left',
                  border: 'none',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[#E2E8F0]">
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs text-[#1B2D5B] font-semibold tracking-widest uppercase">No. {featuredCase.no}</span>
                <span className="h-px flex-1 bg-[#1B2D5B]/20" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#0D1B2A] leading-snug mb-1">
                {featuredCase.name}
              </h3>
              <p className="text-[#1B2D5B] text-sm font-medium mb-4">{featuredCase.location} ／ {featuredCase.category}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {featuredCase.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-[#1B2D5B]/8 text-[#1B2D5B] border border-[#1B2D5B]/20 font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="space-y-3">
                {featuredCase.features.map((f, idx) => (
                  <div key={idx} className="flex gap-3 p-4 rounded-xl bg-white border border-[#E2E8F0]">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#1B2D5B] shrink-0">
                      <i className={`${f.icon} text-white text-sm`} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0D1B2A] mb-0.5 leading-snug">{f.label}</p>
                      <p className="text-xs text-[#666] leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-between">
              <div>
                <p className="text-xs text-[#1B2D5B] tracking-[0.25em] uppercase font-medium mb-4">/ 支援後の成果</p>
                <div className="flex items-start gap-3 p-5 rounded-2xl bg-[#1B2D5B] text-white mb-6">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/15 shrink-0 mt-0.5">
                    <i className="ri-award-line text-base text-white" />
                  </div>
                  <p className="text-sm font-semibold leading-relaxed">{featuredCase.result}</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E2E8F0]">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#EEF6FF] shrink-0">
                      <i className="ri-map-pin-2-line text-[#1B2D5B] text-sm" />
                    </div>
                    <div>
                      <p className="text-xs text-[#888]">エリア</p>
                      <p className="text-sm font-semibold text-[#0D1B2A]">熊本市 ／ 飲食店</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E2E8F0]">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#EEF6FF] shrink-0">
                      <i className="ri-calendar-check-line text-[#1B2D5B] text-sm" />
                    </div>
                    <div>
                      <p className="text-xs text-[#888]">対応期間</p>
                      <p className="text-sm font-semibold text-[#0D1B2A]">制作〜保守継続中</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sub Case Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {subCases.map((c, idx) => (
            <div
              key={c.no}
              className="cs-fade opacity-0 translate-y-6 transition-all duration-700 rounded-2xl overflow-hidden border border-[#E2E8F0] bg-white flex flex-col"
              style={{ transitionDelay: `${(idx + 2) * 100}ms` }}
            >
              {/* Icon Header */}
              <div
                className="h-24 flex items-center justify-center"
                style={{ background: `${c.color}18` }}
              >
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-2xl"
                  style={{ background: c.color }}
                >
                  <i className={`${c.icon} text-2xl text-white`} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-[#1B2D5B] font-semibold tracking-widest uppercase">No. {c.no}</span>
                  <span className="h-px flex-1 bg-[#1B2D5B]/15" />
                  <span
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium"
                    style={{ background: `${c.color}15`, color: c.color }}
                  >
                    <i className={c.icon} />
                    {c.category}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-[#0D1B2A] mb-1 leading-snug">{c.name}</h3>
                <p className="text-xs text-[#888] font-medium mb-3">{c.location}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {c.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-0.5 rounded-full bg-[#1B2D5B]/8 text-[#1B2D5B] border border-[#1B2D5B]/15 font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-auto flex items-center gap-2.5 p-3 rounded-xl bg-[#1B2D5B]/5 border border-[#1B2D5B]/10">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#1B2D5B] shrink-0">
                    <i className={`${c.resultIcon} text-white text-xs`} />
                  </div>
                  <p className="text-xs text-[#1B2D5B] font-semibold leading-snug">{c.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="cs-fade opacity-0 translate-y-6 transition-all duration-700 text-center">
          <p className="text-[#888] text-sm mb-4">さらに多くの実績をご覧になりたい方はお気軽にご相談ください</p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#1B2D5B] text-white text-sm font-semibold hover:bg-[#0D1B2A] transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            無料相談はこちら
          </a>
        </div>

      </div>
    </section>
  );
}
